import React, { useState, useEffect } from "react";
import { useFormikContext, FastField } from "formik";
import { Row, Col, Form } from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { QuotationFormValues } from "../initialValues";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { toast } from "react-toastify";

interface Currency {
  currency_id: string;
  currency_name: string;
  currency_code: string;
  currency_symbol: string;
}

const CurrencyExchangeSection: React.FC = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<QuotationFormValues>();
  const [currencyOptions, setCurrencyOptions] = useState<Currency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Derive selected currency from Formik values and currency options (single source of truth)
  // Use case-insensitive matching and trim whitespace to handle edge cases
  const selectedCurrency =
    currencyOptions.find(
      (c) => c.currency_code?.trim().toUpperCase() === values.currency?.trim().toUpperCase()
    ) || undefined;

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(API_ENDPOINT.MASTERS.CURRENCY.DROPDOWN);
        if (response.data.success) {
          setCurrencyOptions(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
        toast.error("Failed to fetch currencies");
      } finally {
        setLoading(false);
      }
    };

    fetchCurrencies();
  }, []);

  // Set INR as default once currencies load (only if currency is not already set)
  // Also sync currency when editing (when currency is already set)
  useEffect(() => {
    if (!currencyOptions.length) return;

    if (!values.currency) {
      const inr = currencyOptions.find((c) => c.currency_code === "INR");
      if (inr) {
        setFieldValue("currency", inr.currency_code);
        setFieldValue("currency_symbol", inr.currency_symbol);
        setFieldValue("exchange_rate", "1");
      }
    } else {
      // Ensure currency_symbol and exchange_rate are set for existing currency (edit mode)
      // Use case-insensitive matching to handle edge cases
      const matchedCurrency = currencyOptions.find(
        (c) => c.currency_code?.trim().toUpperCase() === values.currency?.trim().toUpperCase()
      );
      if (matchedCurrency) {
        // Always sync currency_symbol to ensure it matches the selected currency
        if (values.currency_symbol !== matchedCurrency.currency_symbol) {
          setFieldValue("currency_symbol", matchedCurrency.currency_symbol);
        }
        // Set exchange_rate to "1" for INR if not set
        if (matchedCurrency.currency_code?.trim().toUpperCase() === "INR" && !values.exchange_rate) {
          setFieldValue("exchange_rate", "1");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyOptions, values.currency]);

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        <h5 className="fw-semibold">Currency & Exchange Details</h5>
      </div>
      <Row
        className="g-3 pb-3 px-1 rounded"
        // style={{ backgroundColor: MecordColors?.LightGrayColor }}
      >
        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>
              Currency<span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              key={`currency-${currencyOptions.length}-${values.currency}`}
              disableClearable
              loading={loading}
              options={currencyOptions}
              getOptionLabel={(option: Currency) =>
                `${option.currency_code} (${option.currency_symbol})`
              }
              isOptionEqualToValue={(option: Currency, value: Currency | null | undefined) => {
                if (!value) return false;
                return option.currency_code?.trim().toUpperCase() === value.currency_code?.trim().toUpperCase();
              }}
              value={selectedCurrency}
              onChange={(_: any, newValue: Currency | null) => {
                if (!newValue) return;

                setFieldValue("currency", newValue.currency_code);
                setFieldValue("currency_symbol", newValue.currency_symbol);
                setFieldValue(
                  "exchange_rate",
                  newValue.currency_code === "INR" ? "1" : ""
                );
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: MecordColors.ActiveBackground,
                          color: MecordColors.ActiveText,
                        }),
                      }}
                    >
                      <span>
                        {option?.currency_code} ({option?.currency_symbol})
                      </span>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params: any) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Currency"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
            {errors.currency && touched.currency && (
              <div className="invalid-feedback d-block">{errors.currency as string}</div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>Exchange Rate (in INR)</Form.Label>
            <FastField name="exchange_rate">
              {({ field, form }: any) => {
                const isCurrentINR = form.values.currency === "INR";
                return (
                  <Form.Control
                    {...field}
                    placeholder={isCurrentINR ? "" : "Enter Exchange Rate"}
                    disabled={isCurrentINR}
                    type="number"
                  />
                );
              }}
            </FastField>
            {errors.exchange_rate && touched.exchange_rate && (
              <div className="invalid-feedback d-block">{errors.exchange_rate as string}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default CurrencyExchangeSection;
